// client/src/services/audienceApi.js
// Audience segments + targeted messaging (Growth Phase 5,
// feature_key "audience_messaging"). Server: /api/audience
// (server/routes/messagingRoutes.js). Staff only; session cookie auth.

import { getJson, patchJson, postJson, putJson, delJson } from './http';

const base = (tenant) => `/api/audience/${tenant}`;

export const CHANNELS = ['email', 'push', 'both'];

// ---- options + settings ----
export const getOptions = (tenant) => getJson(`${base(tenant)}/audience/options`);
export const getSettings = (tenant) => getJson(`${base(tenant)}/settings`);
export const saveSettings = (tenant, body) => putJson(`${base(tenant)}/settings`, body);

// ---- segments ----
export const listSegments = (tenant) => getJson(`${base(tenant)}/segments`);
export const getSegment = (tenant, id) => getJson(`${base(tenant)}/segments/${id}`);
export const createSegment = (tenant, body) => postJson(`${base(tenant)}/segments`, body);
export const updateSegment = (tenant, id, body) => patchJson(`${base(tenant)}/segments/${id}`, body);
export const archiveSegment = (tenant, id) => delJson(`${base(tenant)}/segments/${id}`);
export const refreshSegment = (tenant, id) => postJson(`${base(tenant)}/segments/${id}/refresh`, {});
export const countSegment = (tenant, rules) => postJson(`${base(tenant)}/segments/count`, { rules });

// ---- contacts ----
export const searchContacts = (tenant, { q = '', segmentId } = {}) => {
  const qs = new URLSearchParams();
  if (q) qs.set('q', q);
  if (segmentId) qs.set('segmentId', String(segmentId));
  return getJson(`${base(tenant)}/contacts${qs.toString() ? `?${qs}` : ''}`);
};
export const tagContacts = (tenant, body) => postJson(`${base(tenant)}/contacts/tags`, body);
export const untagContacts = (tenant, body) => postJson(`${base(tenant)}/contacts/tags/remove`, body);

// ---- sends ----
export const listSends = (tenant) => getJson(`${base(tenant)}/sends`);
export const getSend = (tenant, id) => getJson(`${base(tenant)}/sends/${id}`);
export const createSend = (tenant, body) => postJson(`${base(tenant)}/sends`, body);
export const updateSend = (tenant, id, body) => patchJson(`${base(tenant)}/sends/${id}`, body);
export const queueSend = (tenant, id, scheduledAt) =>
  postJson(`${base(tenant)}/sends/${id}/queue`, scheduledAt ? { scheduledAt } : {});
export const cancelSend = (tenant, id) => postJson(`${base(tenant)}/sends/${id}/cancel`, {});
export const testSend = (tenant, id) => postJson(`${base(tenant)}/sends/${id}/test`, {});
export const previewSend = (tenant, body) => postJson(`${base(tenant)}/sends/preview`, body);
export const getReport = (tenant, id) => getJson(`${base(tenant)}/sends/${id}/report`);

/** The server's error text (routes answer { error }), or a fallback. */
export function errorText(err, fallback) {
  return err?.response?.data?.error || err?.response?.data?.message || fallback;
}

/** 409 MIGRATION_NOT_APPLIED from any audience route. */
export function isNotReady(err) {
  return err?.response?.status === 409 || /^409\b/.test(err?.message || '');
}

// ---- rule tree helpers (pure; unit-tested) ----

export const isGroup = (node) => !!node && (Array.isArray(node.all) || Array.isArray(node.any));
export const groupOp = (node) => (Array.isArray(node.any) ? 'any' : 'all');

/** Default settings for a newly added rule of `type`. */
export function newRule(type) {
  if (type === 'total_spend_gte') return { type, amount: 50 };
  if (type === 'tag') return { type, tag: '' };
  if (type === 'email_opt_in') return { type, value: true };
  return { type };
}

/** Drop empty optional settings before sending the tree to the server. */
export function cleanRules(node) {
  if (isGroup(node)) {
    const op = groupOp(node);
    return { [op]: node[op].map(cleanRules) };
  }
  const out = {};
  for (const [k, v] of Object.entries(node || {})) {
    if (v === '' || v === null || v === undefined) continue;
    if (k === 'negate' && !v) continue;
    out[k] = v;
  }
  return out;
}

/** Number of rule leaves (the builder caps at 40, like the server). */
export function countRules(node) {
  if (!isGroup(node)) return 1;
  return node[groupOp(node)].reduce((n, child) => n + countRules(child), 0);
}

/** Insert `token` into `text` at [start, end). Returns { text, caret }. */
export function insertAt(text, token, start, end) {
  const s = Number.isInteger(start) ? start : (text || '').length;
  const e = Number.isInteger(end) ? end : s;
  const value = `${(text || '').slice(0, s)}${token}${(text || '').slice(e)}`;
  return { text: value, caret: s + token.length };
}
